import type { MatchScreen } from '../ui/MatchScreen';

type MatchModule = { MatchScreen: typeof MatchScreen };

/** Set before the one automatic reload, so a chunk that is really gone can't loop. */
const RELOAD_KEY = 'arena.matchChunkReload';

let chunk: Promise<MatchModule> | null = null;

/**
 * The match screen and everything behind it (three.js, the renderer, the lobby preview) is a
 * separate chunk, so the home screen opens without it. Memoized; a failed download is forgotten
 * so the next call retries.
 */
function loadMatchChunk(): Promise<MatchModule> {
  chunk ??= import('../ui/MatchScreen').catch((err: unknown) => {
    chunk = null;
    throw err;
  });
  return chunk;
}

/** Start the download while the room handshake runs; the match screen then renders at once. */
export function preloadMatchScreen(): void {
  loadMatchChunk().catch(() => {}); // the render path retries and reports
}

/**
 * For `React.lazy`. A tab opened before a deploy asks for a chunk hash that no longer exists: reload
 * once to pick up the new build (the session resumes from its reconnection token). If it still
 * fails, render an error instead of unmounting the app.
 */
export async function importMatchScreen(): Promise<{ default: typeof MatchScreen }> {
  try {
    const { MatchScreen } = await loadMatchChunk();
    sessionStorage.removeItem(RELOAD_KEY);
    return { default: MatchScreen };
  } catch (err) {
    if (!sessionStorage.getItem(RELOAD_KEY)) {
      sessionStorage.setItem(RELOAD_KEY, '1');
      window.location.reload();
      return new Promise(() => {}); // keep the fallback up until the page goes
    }
    console.error('Could not load the match screen:', err);
    return { default: MatchLoadFailed };
  }
}

function MatchLoadFailed() {
  return (
    <main className="home">
      <p className="error">Could not load the game. Check your connection and reload the page.</p>
      <button onClick={() => window.location.reload()}>Reload</button>
    </main>
  );
}
